import type {
  ExercisePayload,
  ExerciseType,
  GradeResult,
  AvailabilityBulkInput,
  AvailabilityWindow,
  AvailabilityWindowInput,
  Booking,
  BookingInput,
  BookingPatch,
  BookingStatus,
  Client,
  ClientInput,
  ClientPatch,
  CopyWeekInput,
  Faq,
  Media,
  Purchase,
  PurchaseInput,
  TagStyle,
  TimeOff,
  StudioBookingInput,
  Locale,
  Offer,
  OfferInput,
  OfferPatch,
  Page,
  Product,
  ProductInput,
  ProductPatch,
  Slot,
  Testimonial,
} from '@diana/shared'

export class ApiError extends Error {
  constructor(
    public status: number,
    message: string,
    public issues?: unknown,
  ) {
    super(message)
    this.name = 'ApiError'
  }
}

/**
 * Same-origin fetch. `credentials: 'include'` keeps the session cookie
 * flowing; there is no token to store, and nothing lands in localStorage
 * where a script could read it.
 */
async function request<T>(path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(path, {
    credentials: 'include',
    headers: init.body ? { 'Content-Type': 'application/json' } : undefined,
    ...init,
  })

  if (!res.ok) {
    const body = await res.json().catch(() => ({}) as Record<string, unknown>)
    throw new ApiError(res.status, String(body.error ?? res.statusText), body.issues)
  }

  return res.status === 204 ? (undefined as T) : ((await res.json()) as T)
}

/** What the invite page may show before anybody has proved anything. */
export type InviteInfo = { name: string; email: string }

export type StudentLesson = {
  id: string
  startsAt: string
  endsAt: string
  status: string
  meetingUrl?: string | null
  /** Only on upcoming lessons: still at least 24 hours away, so the student may cancel. */
  canCancel?: boolean
}

export type StudentPurchase = { id: string; kind: string; lessons: number; totalGrosz: number; purchasedAt: string }

/**
 * Everything the student's home page needs, in one request — the same
 * one-round-trip rule the public site follows in useContent.
 *
 * `balance` is null for someone who has an account but no client card yet:
 * they bought a course and never took a 1:1 lesson.
 */
export type StudentHome = {
  profile: { name: string; email: string; locale: Locale; timezone: string }
  lessons: { upcoming: StudentLesson[]; past: StudentLesson[] }
  balance: {
    lessonsDone: number
    lessonsPlanned: number
    paidGrosz: number
    lessonsBought: number
    lessonsRemaining: number
    balanceGrosz: number
  } | null
  purchases: StudentPurchase[]
  courses: { slug: string; title: string }[]
  /** Days in a row with any study activity. */
  streak: number
}

export type ReviewStatus = { today: string; total: number; learnt: number; due: number; streak: number }

export type ReviewCard = {
  id: string
  dueOn: string
  reps: number
  ease: number
  intervalDays: number
  lapses: number
  term: string
  pos: string
  uk: string
  ru: string
  example: string
  lessonNumber: number
  courseLevel: string
}

/* ── the course as a student reads it ─────────────────────────────────── */

export type CourseCard = {
  id: string
  slug: string
  title: string
  subtitle: string
  level: string
  /** false only reaches ADMIN — students never see a draft. */
  published: boolean
  /** false = a lesson plan opened only some of its lessons; `lessons` counts those. */
  whole: boolean
  lessons: number
  completed: number
  next: {
    slug: string
    number: number
    title: string
    titleUk: string
    titleRu: string
    /** Lesson exercises right, over how many there are — homework not included. */
    solved: number
    gradable: number
    started: boolean
  } | null
}

/** One lesson's homework (section 7), as the homework list and the course map read it. */
export type HomeworkState = { parts: number; done: number; score: number; max: number }

export type HomeworkRow = HomeworkState & {
  courseSlug: string
  courseTitle: string
  lessonSlug: string
  number: number
  title: string
  titleUk: string
  titleRu: string
  lessonCompleted: boolean
}

export type LessonSummary = {
  id: string
  slug: string
  number: number
  title: string
  titleUk: string
  titleRu: string
  durationLabel: string
  moduleId: string | null
  completed: boolean
  solved: number
  gradable: number
  homework: HomeworkState | null
  started: boolean
}

export type CourseDetail = {
  id: string
  slug: string
  title: string
  subtitle: string
  level: string
  description: string
  /** false = a lesson plan opened only some lessons, and only those are listed. */
  whole: boolean
  modules: { id: string; title: string; order: number }[]
  lessons: LessonSummary[]
  /** PDFs Diana uploaded — per lesson or for the whole course. */
  materials: { id: string; lessonId: string | null; filename: string; sizeBytes: number }[]
  next: LessonSummary | null
}

export type CourseMediaRow = {
  id: string
  courseId: string
  lessonId: string | null
  kind: 'audio' | 'pdf' | 'image'
  /** "Grafika N" for an image; null otherwise. */
  slot: number | null
  filename: string
  mime: string
  sizeBytes: number
  durationSec: number
  createdAt: string
  /** The row is there but its file is not (lost before the volume existed): upload it again. */
  missing?: boolean
}

export type StudioLessonRow = {
  id: string
  number: number
  slug: string
  title: string
  audioMediaId: string | null
  /** The "Grafika N" numbers the lesson's text asks a student to look at. */
  graphics: number[]
  /** What each picture should show, from section 5 of the lesson file. */
  graphicNotes: Record<number, { title: string; size: string }>
}

export type VocabEntry = {
  id: string
  kind: 'slowo' | 'zwrot'
  term: string
  pos: string
  uk: string
  ru: string
  example: string
}

export type GlossaryEntry = VocabEntry & { lessonNumber: number; lessonSlug: string }

export type Attempt = {
  answers: string[]
  score: number
  max: number
  correct: boolean
  /** The exercise changed since this was graded — worth doing again. */
  stale: boolean
  at: string
  /** Teacher view only: which items were right, marked against today's key. */
  items?: boolean[]
}

export type LessonExercise = {
  id: string
  ref: string
  sectionKey: string
  title: string
  prompt: { pl: string; uk: string; ru: string }
  type: ExerciseType
  autoGradable: boolean
  /** Always stripped of answers — see stripAnswers in @diana/shared. */
  payload: ExercisePayload
  /** The whole exercise without its instruction lines — only for open and unknown exercises. */
  raw: string
  /** What the student reads before the items: a worked example, a word box, a table. */
  lead: string
  attempt: Attempt | null
  /** Teacher view only: the answer key. Never sent to a student. */
  key?: { solution: Solution; guidance: string; rawKey: string }
}

export type SectionKind = 'vocab' | 'grammar' | 'reading' | 'listening' | 'lecture' | 'quiz' | 'homework'

export type LessonFigure = { n: number; sectionKey: string; exerciseRef: string | null; mediaId: string | null }

export type LessonDetail = {
  id: string
  slug: string
  number: number
  title: string
  titleUk: string
  titleRu: string
  goals: string[]
  durationLabel: string
  completedAt: string | null
  /** In tab order. `title` is the heading as written — „Gramatyka  /  Граматика  /  Грамматика”. */
  sections: { id: string; key: string; kind: SectionKind; title: string; body: string }[]
  vocab: VocabEntry[]
  exercises: LessonExercise[]
  audio: { id: string; durationSec: number; mime: string } | null
  /**
   * The pictures the lesson mentions, each placed where it is mentioned: in
   * an exercise (exerciseRef) or under a section's text (exerciseRef null).
   * mediaId is null until Diana uploads it.
   */
  figures: LessonFigure[]
  prev: { slug: string; number: number; title: string } | null
  next: { slug: string; number: number; title: string } | null
}

export type Solution = { n: number; answers: string[]; note: string }[]

export type AnswerResult = GradeResult & { solution: Solution }

export type Enrollment = {
  id: string
  courseId: string
  courseSlug: string
  courseTitle: string
  source: string
  grantedAt: string
  expiresAt: string | null
  revokedAt: string | null
  /** Where the student is: lessons done of how many, and the next one. */
  lessons: number
  completed: number
  nextLesson: number | null
}

export type StudioDashboard = {
  today: string
  lessons: {
    id: string
    startsAt: string
    endsAt: string
    status: BookingStatus
    who: string
    clientId: string | null
    what: string
    fromSite: boolean
    meetingUrl: string
    goals: string
    /** The plan lessons Diana tied to this class. */
    plan: PlanClassItem[]
  }[]
  pendingRequests: number
  newLeads: number
  earnedThisMonthGrosz: number
  lessonsThisMonth: number
  clientsOwing: number
  courses: { id: string; slug: string; title: string; published: boolean; missingRecordings: number }[]
}

export type StudioCourse = {
  id: string
  slug: string
  title: string
  level: string
  published: boolean
  lessons: number
  /** False for a knowledge course: no listening part, so no recordings to upload. */
  hasListening: boolean
  importedAt: string | null
}

export type SiteContent = {
  locale: Locale
  offers: Offer[]
  products: Product[]
  testimonials: Testimonial[]
  faqs: Faq[]
  pages: Record<string, Page>
  settings: Record<string, unknown>
}

export type MediaItem = Media & { url: string }

export type SettingRow = { key: string; locale: string | null; value: unknown }

/** The `contact` setting — what the site shows and where a booking sends people next. */
export type ContactSettings = {
  email?: string
  phone?: string
  telegram?: string
  /** A number ("+48 600 000 000") or a pasted wa.me link — see lib/contact.ts. */
  whatsapp?: string
  responseTime?: string
}

/** A booking as the books see it: with its calendar day in Diana's zone. */
export type LessonRow = {
  id: string
  clientId: string | null
  startsAt: string
  endsAt: string
  status: BookingStatus
  priceGrosz: number
  day: string
}

/** A message from the site's contact form, as Studio's inbox reads it. */
export type StudioLead = {
  id: string
  name: string
  email: string
  message: string
  /** The site language it was sent from: 'pl' | 'uk' | 'ru'. */
  locale: string
  /** Where it came from — 'contact-form' unless a page says otherwise. */
  source: string
  handled: boolean
  createdAt: string
}

export type AccountingData = {
  today: string
  clients: Client[]
  tagStyles: TagStyle[]
  purchases: Purchase[]
  lessons: LessonRow[]
}

/**
 * Everything the week grid draws, including the slots the PUBLIC page would
 * show for the same range — open hours and bookable hours are not the same
 * thing, and the calendar has to be able to show the difference.
 */
export type CalendarWeek = {
  ownerTimezone: string
  minNoticeHours: number
  windows: AvailabilityWindow[]
  bookings: Booking[]
  timeOff: TimeOff[]
  tagStyles: TagStyle[]
  freeSlots: Slot[]
  /** The plan lessons tied to these classes (bookingId says which). */
  planItems: (PlanClassItem & { bookingId: string })[]
}

export type ClientDetail = {
  client: Client
  purchases: Purchase[]
  lessons: (Booking & { day: string })[]
}

/** A tag as the manager sees it: its colour, and whether anyone carries it. */
export type TagInUse = TagStyle & { inUse: boolean }

export type CopyWeekResult = {
  ok: true
  from: string
  to: string
  windowsCopied: number
  windowsSkipped: number
  lessonsCopied: number
  lessonsSkipped: number
  skipped: { name: string; when: string; reason: 'taken' | 'invalid' }[]
}

/* ── lesson plans (Diana's 1:1 and group classes) ─────────────────────── */

/** A plan lesson tied to a class — Start and the calendar show it. lessonSlug null = the whole course. */
export type PlanClassItem = {
  planId: string
  released: boolean
  courseSlug: string
  courseTitle: string
  lessonSlug: string | null
  lessonNumber: number | null
  lessonTitle: string | null
}

/** One member's progress on a plan item: a lesson (lessons 1) or a whole course. */
export type PlanProgress = { lessons: number; completed: number; started: boolean; homework: HomeworkState | null }

export type StudioPlanItem = {
  id: string
  position: number
  /** Shown to the students next to the lesson. */
  note: string
  /** null = not shared yet. */
  releasedAt: string | null
  course: { id: string; slug: string; title: string; published: boolean }
  /** null = the whole course. */
  lesson: { id: string; slug: string; number: number; title: string; retired: boolean } | null
  booking: { id: string; startsAt: string; endsAt: string; status: BookingStatus } | null
  /** Per member with a login. */
  progress: (PlanProgress & { clientId: string })[]
}

export type StudioPlan = {
  id: string
  /** The students' goal, „B1 w czerwcu”. They see it. */
  goal: string
  /** Set = ended: kept, opens nothing. */
  archivedAt: string | null
  createdAt: string
  members: { clientId: string; name: string; hasLogin: boolean }[]
  items: StudioPlanItem[]
  /** The members' recent and coming classes, for tying an item to one. */
  classes: { id: string; clientId: string | null; startsAt: string; endsAt: string; status: BookingStatus }[]
}

/** Every course, drafts included, with its lessons — what can go into a plan. */
export type CourseOutline = {
  id: string
  slug: string
  title: string
  level: string
  published: boolean
  lessons: { id: string; number: number; slug: string; title: string }[]
}[]

/** A lesson in Diana's teacher view: the key, and the chosen student's answers. */
export type TeacherLesson = LessonDetail & {
  course: { slug: string; title: string }
  /** locale: the language of their account (null without one). */
  student: { clientId: string; name: string; hasLogin: boolean; locale: Locale | null } | null
}

type PlanLessonRef = { slug: string; number: number; title: string; titleUk: string; titleRu: string }

/** „Nasz plan” as the student sees it: only what Diana released, in her order. */
export type StudentPlan = {
  id: string
  goal: string
  /** First names of the others sharing the plan. */
  with: string[]
  items: (PlanProgress & {
    id: string
    note: string
    releasedAt: string
    course: { slug: string; title: string }
    /** null = the whole course. */
    lesson: PlanLessonRef | null
    /** For a whole course: where to continue. */
    continueAt: PlanLessonRef | null
    /** The class it is for, when Diana tied it to one. */
    classAt: string | null
  })[]
}

export const api = {
  content: (locale: Locale) => request<SiteContent>(`/api/content?locale=${locale}`),

  subscribe: (email: string, locale: Locale) =>
    request<{ ok: true }>('/api/subscribers', {
      method: 'POST',
      body: JSON.stringify({ email, locale, website: '' }),
    }),

  sendLead: (input: { name: string; email: string; message: string; locale: Locale }) =>
    request<{ ok: true }>('/api/leads', {
      method: 'POST',
      body: JSON.stringify({ ...input, website: '' }),
    }),

  /** Redeeming an invitation. The only calls here that work without a session. */
  invite: {
    peek: (token: string) => request<InviteInfo>(`/api/invite/${encodeURIComponent(token)}`),

    /** Sets the password and signs them in (`signedIn`), so the next page is already theirs. */
    redeem: (token: string, password: string) =>
      request<{ ok: true; signedIn?: boolean }>(`/api/invite/${encodeURIComponent(token)}`, {
        method: 'POST',
        body: JSON.stringify({ password }),
      }),
  },

  /** The signed-in student's own area. Never takes an id — the session is the id. */
  student: {
    me: () => request<StudentHome>('/api/student/me'),
    update: (input: { name?: string; locale?: Locale }) =>
      request<{ ok: true }>('/api/student/me', { method: 'PATCH', body: JSON.stringify(input) }),
    cancel: (bookingId: string) => request<{ ok: true }>(`/api/student/bookings/${bookingId}/cancel`, { method: 'POST' }),
  },

  /** Flashcards over the glossary. Cards belong to the session; no ids of other people. */
  review: {
    status: () => request<ReviewStatus>('/api/course/review'),
    due: () => request<{ today: string; cards: ReviewCard[] }>('/api/course/review/due'),
    lessonsAdded: () => request<string[]>('/api/course/review/lessons'),
    vocabAdded: () => request<string[]>('/api/course/review/vocab'),
    addWord: (vocabId: string) =>
      request<{ added: number }>('/api/course/review/add-word', { method: 'POST', body: JSON.stringify({ vocabId }) }),
    addLesson: (lessonId: string) =>
      request<{ added: number; total: number }>('/api/course/review/add-lesson', {
        method: 'POST',
        body: JSON.stringify({ lessonId }),
      }),
    grade: (cardId: string, grade: 0 | 1 | 2 | 3) =>
      request<{ dueOn: string }>(`/api/course/review/${cardId}`, { method: 'POST', body: JSON.stringify({ grade }) }),
  },

  /** The course, for somebody enrolled in it. Every call is scoped by the session. */
  course: {
    mine: () => request<CourseCard[]>('/api/course/mine'),
    detail: (slug: string) => request<CourseDetail>(`/api/course/courses/${slug}`),
    lesson: (slug: string, lessonSlug: string) =>
      request<LessonDetail>(`/api/course/courses/${slug}/lessons/${lessonSlug}`),
    glossary: (slug: string) => request<GlossaryEntry[]>(`/api/course/courses/${slug}/glossary`),

    answer: (exerciseId: string, answers: string[]) =>
      request<AnswerResult>(`/api/course/exercises/${exerciseId}/answer`, {
        method: 'POST',
        body: JSON.stringify({ answers }),
      }),

    /** For open tasks: the model answer, once they say theirs is written. */
    reveal: (exerciseId: string) =>
      request<{ solution: Solution; guidance: string; rawKey: string }>(`/api/course/exercises/${exerciseId}/reveal`, {
        method: 'POST',
      }),

    homework: () => request<HomeworkRow[]>('/api/course/homework'),

    /** Saves the writing in an open task; nothing is marked. */
    draft: (exerciseId: string, text: string) =>
      request<{ ok: true; savedAt: string }>(`/api/course/exercises/${exerciseId}/draft`, {
        method: 'PUT',
        body: JSON.stringify({ text }),
      }),

    complete: (lessonId: string) =>
      request<{ ok: true }>(`/api/course/lessons/${lessonId}/complete`, { method: 'POST' }),

    /** „Nasz plan”: the student's live lesson plans, released items only. */
    plans: () => request<StudentPlan[]>('/api/course/plans'),
  },

  studio: {
    me: () => request<{ user: { name: string; email: string; role: string } }>('/api/studio/me'),

    /** Start: today's lessons, the three numbers and what needs doing — one request. */
    dashboard: () => request<StudioDashboard>('/api/studio/dashboard'),

    /* lesson plans */
    clientPlans: (clientId: string) => request<StudioPlan[]>(`/api/studio/clients/${clientId}/plans`),
    plan: (planId: string) => request<StudioPlan>(`/api/studio/plans/${planId}`),
    createPlan: (clientIds: string[], goal = '') =>
      request<StudioPlan>('/api/studio/plans', { method: 'POST', body: JSON.stringify({ clientIds, goal }) }),
    /** `archived: true` ends the plan (kept, opens nothing); false brings it back. */
    updatePlan: (planId: string, patch: { goal?: string; archived?: boolean }) =>
      request<StudioPlan>(`/api/studio/plans/${planId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deletePlan: (planId: string) => request<{ ok: true }>(`/api/studio/plans/${planId}`, { method: 'DELETE' }),
    /** Replaces who shares the plan; at least one. */
    setPlanMembers: (planId: string, clientIds: string[]) =>
      request<StudioPlan>(`/api/studio/plans/${planId}/members`, { method: 'PUT', body: JSON.stringify({ clientIds }) }),
    /** lessonId null = the whole course. 409 `duplicate` when it is already in the plan. */
    addPlanItem: (
      planId: string,
      item: { courseId: string; lessonId: string | null; note?: string; bookingId?: string | null; released?: boolean },
    ) => request<StudioPlan>(`/api/studio/plans/${planId}/items`, { method: 'POST', body: JSON.stringify(item) }),
    /** released true shares it now, false hides it (and closes it) again. */
    updatePlanItem: (itemId: string, patch: { note?: string; bookingId?: string | null; released?: boolean }) =>
      request<StudioPlan>(`/api/studio/plan-items/${itemId}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deletePlanItem: (itemId: string) => request<StudioPlan>(`/api/studio/plan-items/${itemId}`, { method: 'DELETE' }),
    /** Every item id of the plan, in the new order. 409 `items_changed` if the list is stale. */
    orderPlan: (planId: string, itemIds: string[]) =>
      request<StudioPlan>(`/api/studio/plans/${planId}/order`, { method: 'PUT', body: JSON.stringify({ itemIds }) }),
    courseOutline: () => request<CourseOutline>('/api/studio/course-outline'),
    /** The natural voice: how many recordings exist of how many, and this month's characters. */
    ttsStatus: () =>
      request<{
        configured: boolean
        generating: boolean
        total: number
        done: number
        missingChars: number
        monthChars: number
        cap: number
        voices: Record<string, string>
      }>('/api/studio/tts'),
    /** The teacher view of a lesson: key included, plus this client's answers when given. */
    teacherLesson: (slug: string, lessonSlug: string, clientId?: string | null) =>
      request<TeacherLesson>(`/api/studio/lessons/${slug}/${lessonSlug}${clientId ? `?client=${clientId}` : ''}`),

    /**
     * Password changes deliberately do NOT go through here — they use
     * better-auth's own client (see routes/studio/AccountPage.tsx).
     */
    changeEmail: (email: string, currentPassword: string) =>
      request<{ ok: true; email: string }>('/api/studio/account/email', {
        method: 'POST',
        body: JSON.stringify({ email, currentPassword }),
      }),

    /**
     * Creates the student's login and the first invite link. The raw link
     * comes back exactly once — it is stored as a hash, so the panel has to
     * offer "new link" rather than "show it again".
     */
    createStudentAccount: (clientId: string, email: string, name: string) =>
      request<{ userId: string; inviteUrl: string }>(`/api/studio/clients/${clientId}/account`, {
        method: 'POST',
        body: JSON.stringify({ email, name }),
      }),

    /** A fresh link. Burns any earlier unused one for the same person. */
    newInvite: (clientId: string) =>
      request<{ inviteUrl: string }>(`/api/studio/clients/${clientId}/invite`, { method: 'POST' }),

    /* ── courses and who may open them ─────────────────────────────────── */
    courses: () => request<StudioCourse[]>('/api/studio/courses'),

    enrollments: (clientId: string) => request<Enrollment[]>(`/api/studio/clients/${clientId}/enrollments`),

    /** "Aktywuj kurs" on a client card. */
    enroll: (clientId: string, courseId: string) =>
      request<Enrollment>(`/api/studio/clients/${clientId}/enrollments`, {
        method: 'POST',
        body: JSON.stringify({ courseId }),
      }),

    revokeEnrollment: (enrollmentId: string) =>
      request<void>(`/api/studio/enrollments/${enrollmentId}`, { method: 'DELETE' }),

    /** Re-imports every course from the files; the summary covers all of them. */
    importCourses: () => request<{ summary: string }>('/api/studio/courses/import', { method: 'POST' }),

    courseStudents: (courseId: string) =>
      request<{ enrollmentId: string; userId: string; name: string; email: string; grantedAt: string; revokedAt: string | null }[]>(
        `/api/studio/courses/${courseId}/students`,
      ),
    courseLessons: (courseId: string) => request<StudioLessonRow[]>(`/api/studio/courses/${courseId}/lessons`),
    courseMedia: (courseId: string) => request<CourseMediaRow[]>(`/api/studio/courses/${courseId}/media`),

    /** Raw bytes with the real Content-Type, like uploadMedia — no multipart. */
    /** `slot` makes an image "Grafika N" of that lesson; images need both. */
    uploadCourseMedia: async (courseId: string, file: File, lessonId: string | null, slot?: number): Promise<CourseMediaRow> => {
      const headers: Record<string, string> = {
        'Content-Type': file.type,
        'X-Filename': encodeURIComponent(file.name),
      }
      if (lessonId) headers['X-Lesson'] = lessonId
      if (slot) headers['X-Slot'] = String(slot)
      const res = await fetch(`/api/studio/courses/${courseId}/media`, {
        method: 'POST',
        credentials: 'include',
        headers,
        body: file,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}) as Record<string, unknown>)
        throw new ApiError(res.status, String(body.error ?? res.statusText))
      }
      return (await res.json()) as CourseMediaRow
    },

    deleteCourseMedia: (id: string) => request<void>(`/api/studio/course-media/${id}`, { method: 'DELETE' }),

    offers: () => request<Offer[]>('/api/studio/offers'),

    updateOffer: (id: string, patch: OfferPatch) =>
      request<Offer>(`/api/studio/offers/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    createOffer: (input: OfferInput) =>
      request<Offer>('/api/studio/offers', { method: 'POST', body: JSON.stringify(input) }),

    deleteOffer: (id: string) => request<void>(`/api/studio/offers/${id}`, { method: 'DELETE' }),

    products: () => request<Product[]>('/api/studio/products'),

    createProduct: (input: ProductInput) =>
      request<Product>('/api/studio/products', { method: 'POST', body: JSON.stringify(input) }),

    updateProduct: (id: string, patch: ProductPatch) =>
      request<Product>(`/api/studio/products/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

    deleteProduct: (id: string) => request<void>(`/api/studio/products/${id}`, { method: 'DELETE' }),

    testimonials: () => request<Testimonial[]>('/api/studio/testimonials'),
    createTestimonial: (input: Omit<Testimonial, 'id'>) =>
      request<Testimonial>('/api/studio/testimonials', { method: 'POST', body: JSON.stringify(input) }),
    updateTestimonial: (id: string, patch: Partial<Omit<Testimonial, 'id'>>) =>
      request<Testimonial>(`/api/studio/testimonials/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deleteTestimonial: (id: string) => request<void>(`/api/studio/testimonials/${id}`, { method: 'DELETE' }),

    faqs: () => request<Faq[]>('/api/studio/faqs'),
    createFaq: (input: Omit<Faq, 'id'>) => request<Faq>('/api/studio/faqs', { method: 'POST', body: JSON.stringify(input) }),
    updateFaq: (id: string, patch: Partial<Omit<Faq, 'id'>>) =>
      request<Faq>(`/api/studio/faqs/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),
    deleteFaq: (id: string) => request<void>(`/api/studio/faqs/${id}`, { method: 'DELETE' }),

    leads: () => request<StudioLead[]>('/api/studio/leads'),

    updateLead: (id: string, patch: { handled: boolean }) =>
      request<StudioLead>(`/api/studio/leads/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

    subscribers: () => request<{ id: string; email: string; createdAt: string }[]>('/api/studio/subscribers'),

    /* ── calendar ─────────────────────────────────────────────────────── */

    availability: (from: string, to: string) =>
      request<{ ownerTimezone: string; windows: AvailabilityWindow[]; bookings: Booking[] }>(
        `/api/studio/availability?from=${from}&to=${to}`,
      ),

    addWindow: (input: AvailabilityWindowInput) =>
      request<AvailabilityWindow>('/api/studio/availability', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    deleteWindow: (id: string) =>
      request<void>(`/api/studio/availability/${id}`, { method: 'DELETE' }),

    updateBooking: (id: string, patch: BookingPatch) =>
      request<Booking>(`/api/studio/bookings/${id}`, {
        method: 'PATCH',
        body: JSON.stringify(patch),
      }),

    /**
     * Open or close a whole selection of hours from the week grid in one
     * request. `close` splits windows rather than deleting them wholesale —
     * see packages/shared/src/availability.ts.
     */
    bulkAvailability: (input: AvailabilityBulkInput) =>
      request<{ ok: true; opened: number; changed: number }>('/api/studio/availability/bulk', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    /** Windows, lessons, time off, groups and the real public slots, in one call. */
    calendarWeek: (from: string, to: string) =>
      request<CalendarWeek>(`/api/studio/calendar/week?from=${from}&to=${to}`),

    /** Every tag she has used, plus every tag that carries a colour. */
    tags: () => request<TagInUse[]>('/api/studio/tags'),

    /** Give a tag a colour. An empty colour removes it; the tag itself stays. */
    setTagColor: (tag: string, color: string) =>
      request<TagStyle>('/api/studio/tags', { method: 'PUT', body: JSON.stringify({ tag, color }) }),

    /** Renames the tag on every student and on its colour, in one transaction. */
    renameTag: (from: string, to: string) =>
      request<{ ok: true; clients: number }>('/api/studio/tags/rename', {
        method: 'POST',
        body: JSON.stringify({ from, to }),
      }),

    /** Copy one week's open hours and chosen students' lessons onto another. */
    copyWeek: (input: CopyWeekInput) =>
      request<CopyWeekResult>('/api/studio/calendar/copy-week', {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    /** A lesson Diana puts in herself, for one of her students. */
    createBooking: (input: StudioBookingInput) =>
      request<Booking>('/api/studio/bookings', { method: 'POST', body: JSON.stringify(input) }),

    /* ── clients & books ──────────────────────────────────────────────── */

    clients: () => request<Client[]>('/api/studio/clients'),

    client: (id: string) => request<ClientDetail>(`/api/studio/clients/${id}`),

    createClient: (input: ClientInput) =>
      request<Client>('/api/studio/clients', { method: 'POST', body: JSON.stringify(input) }),

    updateClient: (id: string, patch: ClientPatch) =>
      request<Client>(`/api/studio/clients/${id}`, { method: 'PATCH', body: JSON.stringify(patch) }),

    deleteClient: (id: string) => request<void>(`/api/studio/clients/${id}`, { method: 'DELETE' }),

    addPurchase: (clientId: string, input: PurchaseInput) =>
      request<Purchase>(`/api/studio/clients/${clientId}/purchases`, {
        method: 'POST',
        body: JSON.stringify(input),
      }),

    deletePurchase: (id: string) => request<void>(`/api/studio/purchases/${id}`, { method: 'DELETE' }),

    accounting: () => request<AccountingData>('/api/studio/accounting'),

    /* ── media & site settings ────────────────────────────────────────── */

    settings: () => request<SettingRow[]>('/api/studio/settings'),

    media: () => request<MediaItem[]>('/api/studio/media'),

    /** Raw bytes, not multipart — see apps/api/src/routes/media.ts. */
    uploadMedia: async (file: File, alt = ''): Promise<MediaItem> => {
      const res = await fetch('/api/studio/media', {
        method: 'POST',
        credentials: 'include',
        // Percent-encoded: a header may only carry Latin-1, and fetch refuses
        // to send „Kurs A0 → A1” or a Cyrillic title at all.
        headers: { 'Content-Type': file.type, 'X-Alt': encodeURIComponent(alt) },
        body: file,
      })
      if (!res.ok) {
        const body = await res.json().catch(() => ({}) as Record<string, unknown>)
        throw new ApiError(res.status, String(body.error ?? res.statusText))
      }
      return (await res.json()) as MediaItem
    },

    deleteMedia: (id: string) => request<void>(`/api/studio/media/${id}`, { method: 'DELETE' }),

    setSetting: (key: string, value: unknown, locale: string | null = null) =>
      request<unknown>(`/api/studio/settings/${key}`, {
        method: 'PUT',
        body: JSON.stringify({ locale, value }),
      }),
  },

  booking: {
    slots: (from: string, to: string) =>
      request<{ ownerTimezone: string; slots: Slot[] }>(`/api/slots?from=${from}&to=${to}`),

    create: (input: BookingInput) =>
      request<{ ok: true; booking: { startsAt: string; endsAt: string; status: string; cancelToken: string } }>(
        '/api/bookings',
        { method: 'POST', body: JSON.stringify({ ...input, website: '' }) },
      ),

    get: (token: string) =>
      request<{ startsAt: string; endsAt: string; name: string; status: string; timezone: string; meetingUrl: string }>(
        `/api/bookings/${token}`,
      ),

    cancel: (token: string) =>
      request<{ ok: true }>(`/api/bookings/${token}/cancel`, { method: 'POST' }),
  },
}
